import { ReactNode, useId } from 'react'
import { Divider, Paper, Stack } from '@mui/material'
import { alpha } from '@mui/material/styles'
import SettingsHeading from './SettingsHeading'

type SettingsSectionProps = {
  /**
   * A short name for the group, like "Document handling". Leave it out only
   * for a page's single card, when the heading would just repeat the page
   * title. The description and actions need a title to show.
   */
  title?: ReactNode
  /** One sentence on what the group controls. */
  description?: ReactNode
  /** Buttons shown on the right of the section heading, such as "Add". */
  actions?: ReactNode
  /**
   * `card` puts the content in a bordered card, with a line between each
   * child — pass `SettingsRow`s. `plain` leaves it bare, for content that
   * brings its own surface, like a table or a list of cards.
   */
  variant?: 'card' | 'plain'
  /** `danger` marks a card of destructive actions, like deleting the project. */
  tone?: 'default' | 'danger'
  /** A bar along the bottom of the card, for a Save button and a short hint. */
  footer?: ReactNode
  /** Usually `SettingsRow`s. */
  children?: ReactNode
}

/**
 * One group of related settings: a heading (an `<h2>`), then its content.
 *
 * By default the content sits in a bordered card with a line between each
 * child, so pass it `SettingsRow`s. Use `variant="plain"` for a table or list
 * that draws its own surface, `footer` for a Save button, and
 * `tone="danger"` for destructive actions.
 */
export default function SettingsSection({
  title,
  description,
  actions,
  variant = 'card',
  tone = 'default',
  footer,
  children,
}: SettingsSectionProps) {
  const headingId = useId()
  const isDanger = tone === 'danger'

  return (
    <Stack
      component="section"
      spacing={1.5}
      aria-labelledby={title ? headingId : undefined}
      data-has-title={title ? true : undefined}
    >
      {title && (
        <SettingsHeading
          level="section"
          id={headingId}
          title={title}
          description={description}
          actions={actions}
        />
      )}

      {variant === 'plain' && children}

      {variant === 'card' && (children || footer) && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            borderColor: (theme) =>
              isDanger ? alpha(theme.palette.error.main, 0.5) : undefined,
          }}
        >
          {children && <Stack divider={<Divider flexItem />}>{children}</Stack>}

          {footer && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={1}
              useFlexGap
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: 1,
                borderColor: (theme) =>
                  isDanger
                    ? alpha(theme.palette.error.main, 0.5)
                    : theme.palette.divider,
                bgcolor: (theme) =>
                  isDanger
                    ? alpha(theme.palette.error.main, 0.06)
                    : alpha(theme.palette.text.primary, 0.03),
              }}
            >
              {footer}
            </Stack>
          )}
        </Paper>
      )}
    </Stack>
  )
}
